import Vue from 'vue';
import types from '../mutation-types';
import authAPI from '../../api/auth';
import wootConstants from 'dashboard/constants/globals';

import {
  setUser,
  clearCookiesOnLogout,
  clearLocalStorageOnLogout,
} from '../utils/api';
import {
  getLoginRedirectURL,
  isValidCallbackUrl,
} from '../../helper/URLHelper';
import RudderStackHelper from '../../helper/RudderStackHelper';
const rudderStack = new RudderStackHelper(RudderStackHelper.RUDDER_STACK_APP);
const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
  },
  isConversationsCardCollapsed: false,
  uiFlags: {
    isFetching: true,
  },
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },
  getIsConversationsCardCollapsed($state) {
    return $state.isConversationsCardCollapsed;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  getAudioNotificationStatus($state) {
    return $state.currentUser.ui_settings.enable_audio_alerts || 'none';
  },

  audioNotificationUnreadAssignedStatus($state) {
    return (
      $state.currentUser.ui_settings
        .alert_if_unread_assigned_conversation_exist || ''
    );
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentUserAutoOffline($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.auto_offline;
  },

  getCurrentUserReceiveIncomingCallsStatus($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.receive_incoming_calls;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.role;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  getAdditionalAttributes($state) {
    return $state.currentUser?.additional_attributes || {};
  },

  getMessageSignature($state) {
    const { message_signature: messageSignature } = $state.currentUser;

    return messageSignature || '';
  },

  getCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount || {};
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    return accounts;
  },
};

// actions
export const actions = {
  login(_, { ssoAccountId, ssoConversationId, callback, ...credentials }) {
    return new Promise((resolve, reject) => {
      authAPI
        .login(credentials)
        .then(async response => {
          clearLocalStorageOnLogout();
          rudderStack.identify(response?.data?.id, {
            name: response?.data?.name,
            email: response?.data?.email,
          });
          if (isValidCallbackUrl(callback)) {
            const { accounts, account_id } = response.data;
            if (accounts.length > 0) {
              const { authorization } = await authAPI.getAuthData();
              const url = new URL(callback);
              const params = new URLSearchParams(url.search);
              // params.append('token', btoa(account_id_signature));
              params.append('account_id', account_id);
              params.append('token', authorization.replace('Bearer ', ''));
              url.search = params.toString();
              window.location = url.toString();
            }
          } else if (callback) {
            // If callback is provided but invalid, proceed with default redirect
            // eslint-disable-next-line no-console
            console.warn(
              'Invalid callback URL provided, using default redirect'
            );
            window.location = getLoginRedirectURL({
              ssoAccountId,
              ssoConversationId,
              user: response.data,
            });
          } else {
            window.location = getLoginRedirectURL({
              ssoAccountId,
              ssoConversationId,
              user: response.data,
            });
          }
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  async validityCheck(context) {
    try {
      const response = await authAPI.validityCheck();
      const currentUser = response.data.payload.data;
      setUser(currentUser);
      context.commit(types.SET_CURRENT_USER, currentUser);
    } catch (error) {
      if (error?.response?.status === 401) {
        clearCookiesOnLogout();
      }
    }
  },
  async setUser({ commit, dispatch }) {
    if (authAPI.hasAuthCookie()) {
      await dispatch('validityCheck');
    } else {
      commit(types.CLEAR_USER);
    }
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
  },
  logout({ commit }) {
    commit(types.CLEAR_USER);
  },

  updateProfile: async ({ commit }, params) => {
    // eslint-disable-next-line no-useless-catch
    try {
      if (params?.additional_attributes?.macros_display_order) {
        const response = await authAPI.updateProfileInfo(params);
        commit(types.SET_CURRENT_USER, response.data);
      } else {
        const response = await authAPI.profileUpdate(params);
        rudderStack.track('profile.updated', {
          name: response?.data?.name,
          email: response?.data?.email,
        });
        rudderStack.identify(response?.data?.id, {
          name: response?.data?.name,
          email: response?.data?.email,
        });
        commit(types.SET_CURRENT_USER, response.data);
      }
    } catch (error) {
      throw error;
    }
  },

  deleteAvatar: async () => {
    try {
      await authAPI.deleteAvatar();
    } catch (error) {
      // Ignore error
    }
  },

  updateUISettings: async ({ commit }, params) => {
    try {
      commit(types.SET_CURRENT_USER_UI_SETTINGS, params);
      commit(
        types.SET_CURRENT_USER_UI_SETTINGS_COLLAPSE,
        params.uiSettings?.conversation_display_type ||
          wootConstants.LAYOUT_TYPES.EXPANDED
      );
      const response = await authAPI.updateUISettings(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  updateAvailability: async ({ commit, dispatch }, params) => {
    try {
      const response = await authAPI.updateAvailability(params);
      const userData = response.data;
      const { id } = userData;
      commit(types.SET_CURRENT_USER, response.data);
      dispatch('agents/updateSingleAgentPresence', {
        id,
        availabilityStatus: params.availability,
      });
    } catch (error) {
      // Ignore error
    }
  },

  updateAutoOffline: async ({ commit }, { accountId, autoOffline }) => {
    try {
      const response = await authAPI.updateAutoOffline(accountId, autoOffline);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  updateReceiveIncomingCallsStatus: async (
    { commit },
    { accountId, canReceiveIncomingCalls }
  ) => {
    try {
      const response = await authAPI.updateCanReceiveIncomingCalls(
        accountId,
        canReceiveIncomingCalls
      );
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, data[$state.currentUser.id]);
    }
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      accounts,
    });
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = initialState.currentUser;
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    Vue.set(_state, 'currentUser', currentUser);
  },
  [types.SET_CURRENT_USER_UI_SETTINGS_COLLAPSE](
    _state,
    conversationDisplayType
  ) {
    const isCollapsed =
      conversationDisplayType === wootConstants.LAYOUT_TYPES.EXPANDED;
    Vue.set(_state, 'isConversationsCardCollapsed', isCollapsed);
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    });
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, { isFetching }) {
    Vue.set(_state, 'uiFlags', { isFetching });
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
